// Ship-from locations: where goods physically leave from when they do not ship
// straight from the vendor — a third-party logistics partner (3PL), our own
// warehouse, a forwarder.
//
// Multi-tenant: each tenant keeps its list in tenants.ship_from_locations
// (JSONB), managed from the Billing & settings page, exactly like receiving
// accounts (shared/payment-accounts.ts). Documents freeze the chosen location's
// name and address at issue time, so editing a location later never changes a
// document already sent.
//
// Used by:
//   - the vendor PO, to tell the vendor where to send the goods;
//   - the commercial invoice, as the ship-from / exporter address and the
//     origin of the FCA delivery term.
//
// Pure (no DB/HTTP) so the rules are unit-testable.

export const SHIP_FROM_KINDS = ['logistics_partner', 'warehouse', 'other'] as const;
export type ShipFromKind = (typeof SHIP_FROM_KINDS)[number];

export const SHIP_FROM_KIND_LABEL: Record<ShipFromKind, string> = {
  logistics_partner: 'Logistics partner (3PL)',
  warehouse: 'Our warehouse',
  other: 'Other',
};

export interface ShipFromLocation {
  /** Stable id, generated on create. Never reused. */
  id: string;
  /** What the broker picks from a list, e.g. "Miami 3PL". */
  label: string;
  kind: ShipFromKind;
  /** Company name printed on documents, e.g. "Acme Logistics LLC". */
  companyName: string;
  /** Full postal address, one line per row. */
  address: string;
  /** City the goods leave from — the origin in "FCA <city>". */
  city: string;
  /** ISO-3166 alpha-2. */
  countryCode: string;
  /** Receiving contact or phone, printed under the address. Optional. */
  contact: string;
  /** Instructions printed on a vendor PO sent to this location, e.g. "Mark
   *  boxes with our PO number. Do not include pricing in the box." Optional. */
  vendorInstructions: string;
  /** Preselected when issuing a document. At most one. */
  isDefault: boolean;
}

export type ShipFromLocationFields = Omit<ShipFromLocation, 'id' | 'isDefault'>;

/** The ship-to block a vendor PO gets for this location. The standard
 *  "no pricing in the box" instruction is a fixed PO term for this ship-to
 *  mode; only location-specific instructions (suite, dock hours) go here. */
export function vendorPoShipToText(l: Pick<ShipFromLocation, 'companyName' | 'address' | 'contact' | 'vendorInstructions'>): string {
  const block = shipFromAddressBlock(l);
  const extra = l.vendorInstructions.trim();
  return extra ? `${block}\n\n${extra}` : block;
}

export function findShipFromLocation(
  locations: ShipFromLocation[] | null | undefined,
  id: string | null | undefined,
): ShipFromLocation | null {
  if (!id) return null;
  return (locations ?? []).find((l) => l.id === id) ?? null;
}

export function defaultShipFromLocation(locations: ShipFromLocation[] | null | undefined): ShipFromLocation | null {
  return (locations ?? []).find((l) => l.isDefault) ?? null;
}

/** At most one default. Unlike receiving accounts, having NO default is valid:
 *  most orders still ship from the vendor. Returns a new array. */
export function withAtMostOneDefault(locations: ShipFromLocation[]): ShipFromLocation[] {
  const first = locations.findIndex((l) => l.isDefault);
  return locations.map((l, i) => ({ ...l, isDefault: i === first }));
}

/** The address block as it is frozen onto a document. */
export function shipFromAddressBlock(l: Pick<ShipFromLocation, 'companyName' | 'address' | 'contact'>): string {
  return [l.companyName, l.address, l.contact].map((s) => s.trim()).filter(Boolean).join('\n');
}
